<template>
  <Navbar />
  <div class="auth-page-shell">
    <div class="reset-container body auth-page-skin" :dir="rtl ? 'rtl' : 'ltr'">

      <div class="reset-card auth-panel">
        <h2>{{ $t('resetOtp.title') }}</h2>
        <p class="subtitle">{{ $t('resetOtp.subtitle') }}</p>

        <form @submit.prevent="handleSendOtp">

          <div class="form-group">
            <label class="form-label">
              {{ $t('resetOtp.phoneNumber') }}
            </label>

            <input
              v-model="phoneNumber"
              type="tel"
              class="form-input"
              :placeholder="$t('resetOtp.phonePlaceholder')"
              @input="validatePhone"
            />

            <div class="error-message" v-if="phoneError">
              {{ phoneError }}
            </div>
          </div>

          <button
            type="submit"
            class="submit-btn"
            :disabled="!isValidPhone || loading"
          >
            {{ loading ? $t('resetOtp.sending') : $t('resetOtp.sendOtp') }}
          </button>

          <div class="back-to-login">
            <router-link to="/login">
              {{ $t('resetOtp.backToLogin') }}
            </router-link>
          </div>

        </form>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import Navbar from '@/components/navbar_styled_register.vue';

export default {
  name: 'ResetPasswordPhone',
  components: { Navbar },
  setup() {
    const router = useRouter()
    const phoneNumber = ref('')
    const loading = ref(false)
    const phoneError = ref('')
    const currentLang = ref('fa')

    const translations = {
      en: {
        title: "Reset Password",
        subtitle: "Enter your phone number to receive OTP",
        phoneNumber: "Phone Number",
        phonePlaceholder: "Enter your phone number",
        sendOtp: "Send OTP",
        sending: "Sending...",
        backToLogin: "Back to Login",
        invalidPhone: "Please enter a valid phone number"
      },
      fa: {
        title: "بازنشانی رمز عبور",
        subtitle: "برای دریافت کد تأیید، شماره تلفن خود را وارد کنید",
        phoneNumber: "شماره تلفن",
        phonePlaceholder: "شماره تلفن خود را وارد کنید",
        sendOtp: "ارسال کد تأیید",
        sending: "در حال ارسال...",
        backToLogin: "بازگشت به ورود",
        invalidPhone: "لطفاً یک شماره تلفن معتبر وارد کنید"
      },
      ar: {
        title: "إعادة تعيين كلمة المرور",
        subtitle: "أدخل رقم هاتفك لتلقي رمز التحقق",
        phoneNumber: "رقم الهاتف",
        phonePlaceholder: "أدخل رقم هاتفك",
        sendOtp: "إرسال رمز التحقق",
        sending: "جاري الإرسال...",
        backToLogin: "العودة إلى تسجيل الدخول",
        invalidPhone: "الرجاء إدخال رقم هاتف صحيح"
      }
    }

    const direction = computed(() => {
      return currentLang.value === 'fa' || currentLang.value === 'ar' ? 'rtl' : 'ltr'
    })

    const isValidPhone = computed(() => {
      return phoneNumber.value.length >= 10 && !phoneError.value
    })

    const validatePhone = () => {
      const phone = phoneNumber.value.replace(/\D/g, '')
      if (phone.length < 10) {
        phoneError.value = translations[currentLang.value].invalidPhone
      } else {
        phoneError.value = ''
      }
    }

    const switchLanguage = () => {
      localStorage.setItem('selectedLanguage', currentLang.value)
    }

    const handleSendOtp = async () => {
      if (!isValidPhone.value) return

      loading.value = true

      try {
        // Call the send-otp endpoint for password reset
        const response = await api.post('/send-otp', {
          phone_number: phoneNumber.value
        })

        if (response.data.success) {
          // Save phone number in localStorage for next steps
          localStorage.setItem('resetPhoneNumber', phoneNumber.value)
          // Navigate to OTP verification view
          router.push('/reset-otp')
        } else {
          throw new Error(response.data.message || 'Failed to send OTP')
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to send OTP. Please try again.')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      const savedLang = localStorage.getItem('selectedLanguage') || 'fa'
      currentLang.value = savedLang
    })

    return {
      phoneNumber,
      loading,
      phoneError,
      currentLang,
      translations,
      direction,
      isValidPhone,
      validatePhone,
      switchLanguage,
      handleSendOtp
    }
  }
}
</script>

<style scoped>

.reset-container{
  display:flex;
  justify-content:center;
  align-items:center;
  min-height:100vh;
  background:var(--bg);
  padding:1rem;
}

.reset-card{
  background:var(--card-bg);
  color:var(--text);
  padding:2rem;
  border-radius:12px;
  border:1px solid var(--border);
  box-shadow:var(--shadow);
  width:100%;
  max-width:420px;
}

.reset-card h2{
  text-align:center;
  color:var(--primary);
}

.subtitle{
  text-align:center;
  color:var(--text-secondary);
  margin-bottom:1.5rem;
}

.form-group{
  margin-bottom:1.5rem;
}

.form-label{
  display:block;
  margin-bottom:0.4rem;
  font-weight:500;
}

.form-input{
  width:100%;
  padding:0.75rem;
  border-radius:6px;
  border:1px solid var(--border);
  background:var(--input-bg);
  color:var(--text);
}

.form-input:focus{
  border-color:var(--primary);
  outline:none;
}

.error-message{
  color: black;
  font-size:0.8rem;
  margin-top:0.25rem;
}

.submit-btn{
  width:100%;
  background:var(--primary);
  color:white;
  border:none;
  padding:0.75rem;
  border-radius:6px;
  cursor:pointer;
  margin-top:1rem;
}

.submit-btn:disabled{
  background:var(--disabled);
}

.back-to-login{
  text-align:center;
  margin-top:1.5rem;
}

.back-to-login a{
  color:var(--primary);
}

.language-switcher{
  position:absolute;
  top:1rem;
  right:1rem;
}

.language-dropdown-select{
  padding:0.4rem;
  border-radius:6px;
  border:1px solid var(--border);
  background:var(--input-bg);
  color:var(--text);
}


/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}

.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }

}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 768px) {
  .reset-card{
    
    position: relative;
    z-index: 4;
    width: min(100%, 400px);
    padding: 28px 22px;
    background: #00000000;
    border-color: color-mix(in srgb, var(--border) 86%, var(--text-primary) 14%);
    box-shadow: 0 18px 44px color-mix(in srgb, var(--card-shadow) 82%, transparent);
  }
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    margin-top: 150px !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
  .reset-card {
    margin-top: 150px
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}

.otp-container.auth-page-skin,
.verify-container.auth-page-skin,
.password-container.auth-page-skin,
.reset-container.auth-page-skin,
.login-container.auth-page-skin {
  height: auto !important;
  min-height: 100dvh !important;
}

</style>
